import { formatCurrency } from '@koob/utils';
import { dayjs, formatDate, DATE_US } from '@koob/dates';
import ReceiptContentItemType from './ReceiptContentItemType';
import { useMemo } from 'react';
import {
  KoobBadge,
  KoobInputNumber,
  KoobTableRow,
  KoobTableCell,
} from '@koob/ui';
import { useTranslation } from 'react-i18next';

export const receiptItemDateFormatter = (startAt, endAt) => {
  if (dayjs(startAt).isSame(endAt, 'day') || !endAt) {
    return formatDate(startAt, DATE_US);
  }
  return `${formatDate(startAt, 'MMM DD')} — ${formatDate(endAt, DATE_US)}`;
};

export default function ReceiptContentItem({
  item,
  onRemove,
  setAllItems,
  showConvertedColumn,
  targetCurrency,
}) {
  const { t } = useTranslation('folders');
  const formattedDate = useMemo(() => {
    if (!item.startAt && !item.endAt) {
      return '';
    }
    return receiptItemDateFormatter(item.startAt, item.endAt);
  }, [item.startAt, item.endAt]);

  const handleMarkupItem = ({
    id,
    type,
    markup = item.markup,
    isMarkupPercent = item.isMarkupPercent,
  }) => {
    const startAt = item.startAt;
    const markupValue = markup ?? 0;
    setAllItems((prevItems) =>
      prevItems.map((item) =>
        item.id === id && item.type === type && item.startAt === startAt
          ? {
              ...item,
              markup: markupValue,
              isMarkupPercent: isMarkupPercent,
              price: isMarkupPercent
                ? item.priceWithoutMarkup * (1 + markupValue / 100)
                : item.priceWithoutMarkup + (markupValue ?? 0),
            }
          : item,
      ),
    );
  };

  return (
    <KoobTableRow>
      <KoobTableCell>
        <div className="whitespace-normal">
          <div className="flex space-x-3 items-center">
            <ReceiptContentItemType type={item.type} />

            <div>
              <div className="font-medium text-base text-gray-600">
                {item.name}
              </div>
              <div>{item.location}</div>
            </div>

            {item.state === 'canceled' && (
              <KoobBadge color="red">
                {t('bookings.status.cancelled')}
              </KoobBadge>
            )}
          </div>
        </div>
      </KoobTableCell>
      <KoobTableCell>{formattedDate}</KoobTableCell>

      <KoobTableCell>
        {item.pax && (
          <>
            <i className="far fa-user mr-1" />
            {item.pax}
          </>
        )}
      </KoobTableCell>

      {showConvertedColumn ? (
        <KoobTableCell>
          <div
            className={[
              'text-right export-hidden',
              item.state === 'canceled' ? 'line-through text-gray-500' : '',
            ].join(' ')}
          >
            {formatCurrency({
              amount: item.priceWithoutMarkup ?? item.price,
              currency: item.currency,
            })}
          </div>

          {item.state === 'canceled' && (
            <div className="text-right font-medium text-red-500 export-hidden">
              {formatCurrency({
                amount: item.cancellationFee,
                currency: item.currency,
              })}
            </div>
          )}
        </KoobTableCell>
      ) : null}

      <KoobTableCell>
        <div className="export-hidden flex items-center space-x-2">
          <div className="w-1/2">
            <KoobInputNumber
              size="sm"
              min={0}
              value={item?.markup}
              emptyValue={0}
              onValueChange={(markup) =>
                handleMarkupItem({
                  id: item.id,
                  type: item.type,
                  markup: markup ?? 0,
                })
              }
              placeholder={t('trips:receipt.markup.valuePlaceholder')}
            />
          </div>
          <div className="flex items-center space-x-2">
            <input
              checked={item.isMarkupPercent}
              type="checkbox"
              onChange={(event) => {
                handleMarkupItem({
                  id: item.id,
                  type: item.type,
                  isMarkupPercent: event.target.checked,
                });
              }}
            />
            <i className="fa-solid fa-percent" />
          </div>
        </div>
      </KoobTableCell>

      <KoobTableCell>
        <div
          className={[
            'text-right',
            item.state === 'canceled' ? 'line-through text-gray-500' : '',
          ].join(' ')}
        >
          {showConvertedColumn
            ? item.convertedPrice != null
              ? formatCurrency({
                  amount: item.convertedPrice,
                  currency: targetCurrency,
                  noDecimals: true,
                })
              : ''
            : formatCurrency({
                amount: item.price,
                currency: item.currency,
              })}
        </div>

        {!showConvertedColumn && item.state === 'canceled' && (
          <div className="text-right font-medium text-red-500">
            {formatCurrency({
              amount: item.cancellationFee,
              currency: item.currency,
            })}
          </div>
        )}
      </KoobTableCell>
      {item.type === 'manual' && (
        <KoobTableCell>
          <div className="export-hidden ml-2" onClick={() => onRemove()}>
            <i className="fa-regular fa-trash" />
          </div>
        </KoobTableCell>
      )}
    </KoobTableRow>
  );
}
